import type { MarkOptional } from 'ts-essentials'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type {
  ActivityGroup,
  ActivityGroupActivityId,
  ActivityGroupActivity,
  ActivityGroupRelation,
  ActivityGroupId,
  ActivityGroupRelationId,
} from '@/types'

import BaseService from './BaseService'
import ActivityGroupActivityService from './ActivityGroupActivityService'
import type {
  ActivityGroupFilter,
  UpdateActivityGroupActivityPayload,
} from './ActivityGroupService.types'

export default class ActivityGroupService extends BaseService<
  ActivityGroup,
  Partial<ActivityGroup>,
  ActivityGroupFilter
> {
  constructor(
    client: AxiosInstance,
    private readonly activityGroupActivityService: ActivityGroupActivityService
  ) {
    super(client, 'activity-group')
  }

  async relation(id: ActivityGroupActivityId): Promise<ActivityGroupActivity> {
    const { data } = await this.client.get<ActivityGroupActivity>(
      `/activity-group-activity/${id}`
    )
    return data
  }

  async urelation(
    id: ActivityGroupActivityId,
    payload: UpdateActivityGroupActivityPayload
  ): Promise<ActivityGroupActivity> {
    const { data } = await this.client.patch(
      `/activity-group-activity/${id}`,
      payload
    )
    return data
  }

  async addActivity(
    activityGroupId: number,
    activityId: number,
    position: number
  ): Promise<ActivityGroupActivity> {
    const { data } = await this.client.post('/activity-group-activity', {
      activityGroupId,
      activityId,
      position,
    })
    return data
  }

  async removeActivity(
    activityGroupActivityId: ActivityGroupActivityId
  ): Promise<ActivityGroupActivityId> {
    await this.client.delete(
      `/activity-group-activity/${activityGroupActivityId}`
    )
    return activityGroupActivityId
  }

  async link(
    relation: MarkOptional<Omit<ActivityGroupRelation, 'id'>, 'settings'>
  ): Promise<ActivityGroupRelation> {
    const { data } = await this.client.post<ActivityGroupRelation>(
      '/activity-group-relation',
      relation
    )
    return data
  }

  async unlink(
    activityGroupRelationId: ActivityGroupRelationId
  ): Promise<void> {
    await this.client.delete(
      `/activity-group-relation/${activityGroupRelationId}`
    )
  }

  async linkBulk(
    payload: MarkOptional<Omit<ActivityGroupRelation, 'id'>, 'settings'>[]
  ): Promise<ActivityGroupRelation[]> {
    const { data } = await this.client.post<ActivityGroupRelation[]>(
      '/activity-group-relation/bulk',
      {
        bulk: payload,
      }
    )
    return data
  }

  async updateLink(
    activityGroupRelationId: number,
    payload: Partial<Pick<ActivityGroupRelation, 'rightId' | 'settings'>>
  ): Promise<ActivityGroupRelation> {
    const { data } = await this.client.patch<ActivityGroupRelation>(
      `/activity-group-relation/${activityGroupRelationId}`,
      payload
    )
    return data
  }

  async getActivityRelation(
    id: ActivityGroupActivity['id'],
    config?: AxiosRequestConfig
  ): Promise<ActivityGroupActivity> {
    const { data } = await this.client.get<ActivityGroupActivity>(
      `/activity-group-activity/${id}`,
      config
    )
    return data
  }

  async copy(id: ActivityGroupId, name: string): Promise<ActivityGroupId> {
    const { data } = await this.client.post<ActivityGroupId>(
      `/${this.entity}/${id}`,
      {
        name,
      }
    )
    return data
  }

  async copyMany(ids: ActivityGroupId[]): Promise<ActivityGroupId[]> {
    const { data } = await this.client.post<ActivityGroupId[]>(
      `/${this.entity}/bulk`,
      {
        ids,
      }
    )
    return data
  }

  async updateActivity(
    ...args: Parameters<ActivityGroupActivityService['updateActivity']>
  ): ReturnType<ActivityGroupActivityService['updateActivity']> {
    return this.activityGroupActivityService.updateActivity(...args)
  }

  protected s(filter: ActivityGroupFilter, params: Record<string, unknown>) {
    if (filter.status !== undefined) {
      params.status = { $eq: filter.status }
    }
  }

  static ACTIVITIES_RELATION = 'activities'
  static ROOMS_RELATION = 'rooms'
  static RELATIONS_RELATION = 'relations'
  static ACTIVITIES_ACTIVITY_RELATION = 'activities.activity'
  static ACTIVITIES_ACTIVITY_STEPS_RELATION = 'activities.activity.steps'
  static ACTIVITIES_ACTIVITY_STEPS_MEDIA_RELATION =
    'activities.activity.steps.media'
  static ACTIVITIES_ACTIVITY_STEPS_MEDIA_MEDIA_RELATION =
    'activities.activity.steps.media.media'
  static ROOMS_ROOM_RELATION = 'rooms.room'
  static UPDATED_BY = 'updatedBy'
}
